import { useTranslation } from 'react-i18next';
import { ArrowUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { FfcTransfer } from '@/types';
import { formatDateByDayjs, formatWithThousandSeparator } from '@/utils';

interface FfcTransfersListProps {
  transfers: FfcTransfer[];
  total: number;
  page: number;
  perPage: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
}

/**
 * "Transfers to FFC" list (DESIGN State A §6) — each settlement repayment
 * Boxy sent to FFC, with the equal credit restored. Money is `<bdi>`
 * LTR-isolated + tabular-nums. Server-paginated against the house
 * items/page/total/perPage envelope (same footer as the batches table).
 * Empty list renders the house empty copy.
 */
function FfcTransfersList({
  transfers,
  total,
  page,
  perPage,
  loading,
  onPageChange,
}: FfcTransfersListProps) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const shownTo = Math.min(page * perPage, total);

  return (
    <div className='overflow-hidden rounded-2xl border border-solid border-[#EBEBE6] bg-white'>
      <div className='px-[22px] pb-3.5 pt-[18px]'>
        <div className='text-[16px] font-extrabold tracking-[-0.3px] text-[#1B1B18]'>
          {String(t('advance_cod_transfers_title'))}
        </div>
        <div className='mt-1 text-[12.5px] text-[#9A9A92]'>
          {String(t('advance_cod_transfers_subtitle'))}
        </div>
      </div>

      {loading && transfers.length === 0 ? (
        <div className='flex justify-center border-t border-solid border-[#F2F2ED] py-10'>
          <div className='h-7 w-7 animate-spin rounded-full border-2 border-[rgb(237,224,74)] border-t-transparent' />
        </div>
      ) : transfers.length === 0 ? (
        <div className='border-t border-solid border-[#F2F2ED] px-[22px] py-10 text-center'>
          <span className='text-[13px] text-[#9A9A92]'>
            {String(t('advance_cod_no_transfers'))}
          </span>
        </div>
      ) : (
        transfers.map((transfer, index) => (
          <div
            key={transfer.uid ?? transfer.reference ?? index}
            className='flex items-center gap-4 border-t border-solid border-[#F2F2ED] px-[22px] py-[15px]'
            data-testid='advance-cod-transfer-row'
          >
            <span className='inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] bg-[#E7F1EA]'>
              <ArrowUp className='h-[17px] w-[17px]' style={{ color: '#15794B' }} strokeWidth={2} />
            </span>
            <div className='min-w-0 flex-1'>
              <div className='text-[14px] font-bold text-[#1B1B18]'>
                <bdi>{transfer.reference ?? '—'}</bdi>
              </div>
              <div className='mt-0.5 text-[12.5px] text-[#9A9A92]'>
                <bdi>{formatDateByDayjs(transfer.date ?? undefined, 'D MMMM YYYY')}</bdi>
                {' · '}
                {String(
                  t('advance_cod_covers_batches', { count: transfer.batches_covered_count ?? 0 }),
                )}
              </div>
            </div>
            <div className='text-end'>
              <div
                className='text-[14.5px] font-bold text-[#1B1B18]'
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                <bdi dir='ltr'>
                  {formatWithThousandSeparator(Number(transfer.amount ?? 0))} {String(t('iqd'))}
                </bdi>
              </div>
              <div className='mt-0.5 text-[12px] font-bold text-[#15794B]'>
                <bdi dir='ltr'>
                  +{formatWithThousandSeparator(Number(transfer.credit_restored_amount ?? 0))}{' '}
                  {String(t('iqd'))}
                </bdi>{' '}
                {String(t('advance_cod_credit_restored'))}
              </div>
            </div>
          </div>
        ))
      )}

      {/* footer / pagination — house pattern shared with the batches table */}
      {transfers.length > 0 ? (
        <div className='flex items-center justify-end gap-3.5 border-t border-solid border-[#F2F2ED] px-[22px] py-4'>
          <span className='text-[13px] font-semibold text-[#9A9A92]'>
            {String(t('advance_cod_show_records', { shown: shownTo, total }))}
          </span>
          <div className='flex gap-1.5'>
            <button
              type='button'
              aria-label={String(t('previous'))}
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              className='inline-flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-solid border-[#EBEBE6] bg-white disabled:cursor-not-allowed disabled:opacity-40'
            >
              <ChevronLeft
                className='h-[15px] w-[15px] text-[#B4B4AC] rtl:rotate-180'
                strokeWidth={2.2}
              />
            </button>
            <span className='inline-flex h-[34px] min-w-[34px] items-center justify-center rounded-[9px] bg-[rgb(237,224,74)] px-2 text-[13.5px] font-bold text-[#1B1B18]'>
              {page}
            </span>
            <button
              type='button'
              aria-label={String(t('next'))}
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
              className='inline-flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-solid border-[#EBEBE6] bg-white disabled:cursor-not-allowed disabled:opacity-40'
            >
              <ChevronRight
                className='h-[15px] w-[15px] text-[#B4B4AC] rtl:rotate-180'
                strokeWidth={2.2}
              />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default FfcTransfersList;
